import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaCheckCircle,
  FaLeaf,
  FaCertificate,
  FaBarcode,
  FaDownload,
  FaBoxOpen,
  FaShieldAlt,
  FaSeedling,
  FaMortarPestle,
  FaPepperHot,
  FaPrescriptionBottle,
  FaUtensils,
  FaBox,
  FaQuestionCircle,
  FaChevronDown,
  FaChevronUp,
  FaIndustry,
  FaBlender,
  FaBreadSlice,
  FaGlassWhiskey,
  FaTimes,
  FaFlask,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const OrganicSpices = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const products = [
    {
      name: t("organicSpices.products.0.name"),
      images: [
        "/images/products/turmeric-powder-pack.webp",
        "/images/products/turmeric-powder-closeup.webp",
      ],
      emoji: "🟡",
      color: "from-yellow-400 to-orange-500",
      appearance: t("organicSpices.products.0.appearance"),
      aroma: t("organicSpices.products.0.aroma"),
      flavour: t("organicSpices.products.0.flavour"),
      texture: t("organicSpices.products.0.texture"),
      ingredient: t("organicSpices.products.0.ingredient"),
      uses: [
        t("organicSpices.products.0.uses.0"),
        t("organicSpices.products.0.uses.1"),
        t("organicSpices.products.0.uses.2"),
        t("organicSpices.products.0.uses.3"),
        t("organicSpices.products.0.uses.4"),
      ],
      shelfLife: t("organicSpices.products.0.shelfLife"),
      active: t("organicSpices.products.0.active"),
      dataSheet: "/images/DataSheet/turmeric-powder.jpg",
    },
    {
      name: t("organicSpices.products.1.name"),
      images: [
        "/images/products/ginger-powder-pack.webp",
        "/images/products/ginger-powder-texture.webp",
      ],
      emoji: "🫚",
      color: "from-amber-200 to-yellow-600",
      appearance: t("organicSpices.products.1.appearance"),
      aroma: t("organicSpices.products.1.aroma"),
      flavour: t("organicSpices.products.1.flavour"),
      texture: t("organicSpices.products.1.texture"),
      ingredient: t("organicSpices.products.1.ingredient"),
      uses: [
        t("organicSpices.products.1.uses.0"),
        t("organicSpices.products.1.uses.1"),
        t("organicSpices.products.1.uses.2"),
        t("organicSpices.products.1.uses.3"),
      ],
      shelfLife: t("organicSpices.products.1.shelfLife"),
      active: t("organicSpices.products.1.active"),
      dataSheet: "/images/DataSheet/ginger-powder.jpg",
    },
    {
      name: t("organicSpices.products.2.name"),
      images: [
        "/images/products/cinnamon-ceylon-sticks.webp",
        "/images/products/cinnamon-ceylon-powder.webp",
      ],
      emoji: "🟤",
      color: "from-amber-600 to-brown-500",
      appearance: t("organicSpices.products.2.appearance"),
      aroma: t("organicSpices.products.2.aroma"),
      flavour: t("organicSpices.products.2.flavour"),
      texture: t("organicSpices.products.2.texture"),
      ingredient: t("organicSpices.products.2.ingredient"),
      uses: [
        t("organicSpices.products.2.uses.0"),
        t("organicSpices.products.2.uses.1"),
        t("organicSpices.products.2.uses.2"),
        t("organicSpices.products.2.uses.3"),
      ],
      shelfLife: t("organicSpices.products.2.shelfLife"),
      active: t("organicSpices.products.2.active"),
      dataSheet: "/images/DataSheet/cinnamon-ceylon.jpg",
    },
    {
      name: t("organicSpices.products.3.name"),
      images: [
        "/images/products/black-pepper-whole-pack.webp",
        "/images/products/black-pepper-whole-berries.webp",
      ],
      emoji: "⚫",
      color: "from-gray-700 to-black",
      appearance: t("organicSpices.products.3.appearance"),
      aroma: t("organicSpices.products.3.aroma"),
      flavour: t("organicSpices.products.3.flavour"),
      texture: t("organicSpices.products.3.texture"),
      ingredient: t("organicSpices.products.3.ingredient"),
      uses: [
        t("organicSpices.products.3.uses.0"),
        t("organicSpices.products.3.uses.1"),
        t("organicSpices.products.3.uses.2"),
      ],
      shelfLife: t("organicSpices.products.3.shelfLife"),
      active: t("organicSpices.products.3.active"),
      dataSheet: "/images/DataSheet/black-pepper-whole.jpg",
    },
    {
      name: t("organicSpices.products.4.name"),
      images: [
        "/images/products/black-pepper-powder-pack.webp",
        "/images/products/black-pepper-powder-texture.webp",
      ],
      emoji: "⚫",
      color: "from-gray-600 to-gray-900",
      appearance: t("organicSpices.products.4.appearance"),
      aroma: t("organicSpices.products.4.aroma"),
      flavour: t("organicSpices.products.4.flavour"),
      texture: t("organicSpices.products.4.texture"),
      ingredient: t("organicSpices.products.4.ingredient"),
      uses: [
        t("organicSpices.products.4.uses.0"),
        t("organicSpices.products.4.uses.1"),
        t("organicSpices.products.4.uses.2"),
        t("organicSpices.products.4.uses.3"),
      ],
      shelfLife: t("organicSpices.products.4.shelfLife"),
      active: t("organicSpices.products.4.active"),
      dataSheet: "/images/DataSheet/black-pepper-powder.jpg",
    },
  ];

  // Navigate images
  const nextImage = () => {
    setActiveImageIndex((prev) =>
      prev === products[activeProduct].images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? products[activeProduct].images.length - 1 : prev - 1
    );
  };

  const DownloadModal = () => (
    <AnimatePresence>
      {showDownloadModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDownloadModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed */}
            <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white p-6 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-2xl font-bold mb-1">
                  {t("organicSpices.downloadModal.title")}
                </h3>
                <p className="text-orange-100 text-sm">
                  {t("organicSpices.downloadModal.subtitle")}
                </p>
              </div>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-300"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Product List - Scrollable */}
            <div className="p-6 space-y-3 overflow-y-auto flex-1">
              {products.map((product, index) => (
                <motion.a
                  key={index}
                  href={product.dataSheet}
                  download
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-700 dark:to-gray-600 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Product Icon */}
                    <div
                      className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center text-3xl shadow-md flex-shrink-0`}
                    >
                      {product.emoji}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors truncate">
                        {product.name}
                      </h4>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-gray-600 dark:text-gray-400">
                        <span className="flex items-center space-x-1">
                          <FaFlask className="text-orange-600" />
                          <span>{product.active}</span>
                        </span>
                        <span className="w-px h-3 bg-gray-300 dark:bg-gray-500"></span>
                        <span className="flex items-center space-x-1">
                          <FaBoxOpen className="text-orange-600" />
                          <span>{product.shelfLife}</span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {t("organicSpices.downloadModal.productInfo")}
                      </p>
                    </div>
                  </div>

                  {/* Download Icon */}
                  <div className="bg-orange-600 text-white rounded-full p-3 group-hover:bg-orange-700 group-hover:scale-110 transition-all duration-300 shadow-md flex-shrink-0 ml-4">
                    <FaDownload className="text-xl" />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const commonFeatures = [
    {
      icon: <FaSeedling />,
      title: t("organicSpices.commonFeatures.0.title"),
      desc: t("organicSpices.commonFeatures.0.desc"),
    },
    {
      icon: <FaMortarPestle />,
      title: t("organicSpices.commonFeatures.1.title"),
      desc: t("organicSpices.commonFeatures.1.desc"),
    },
    {
      icon: <FaBoxOpen />,
      title: t("organicSpices.commonFeatures.2.title"),
      desc: t("organicSpices.commonFeatures.2.desc"),
    },
    {
      icon: <FaLeaf />,
      title: t("organicSpices.commonFeatures.3.title"),
      desc: t("organicSpices.commonFeatures.3.desc"),
    },
  ];

  const applications = [
    { icon: <FaUtensils />, text: t("organicSpices.applications.0") },
    { icon: <FaPepperHot />, text: t("organicSpices.applications.1") },
    { icon: <FaBreadSlice />, text: t("organicSpices.applications.2") },
    { icon: <FaGlassWhiskey />, text: t("organicSpices.applications.3") },
    { icon: <FaPrescriptionBottle />, text: t("organicSpices.applications.4") },
    { icon: <FaSeedling />, text: t("organicSpices.applications.5") },
  ];

  const certifications = [
    {
      name: t("dehydratedFruits.certifications.brc.name"),
      image: "/images/certifications/BRC.webp",
      alt: t("dehydratedFruits.certifications.brc.name"),
      description: t("dehydratedFruits.certifications.brc.description"),
      icon: "🏆",
    },
    {
      name: t("dehydratedFruits.certifications.eu.name"),
      image: "/images/certifications/EU.webp",
      alt: t("dehydratedFruits.certifications.eu.name"),
      description: t("dehydratedFruits.certifications.eu.description"),
      icon: "🇪🇺",
    },
    {
      name: t("dehydratedFruits.certifications.usda.name"),
      image: "/images/certifications/USDA.webp",
      alt: t("dehydratedFruits.certifications.usda.name"),
      description: t("dehydratedFruits.certifications.usda.description"),
      icon: "🇺🇸",
    },
    {
      name: t("dehydratedFruits.certifications.jas.name"),
      image: "/images/certifications/JAS.webp",
      alt: t("dehydratedFruits.certifications.jas.name"),
      description: t("dehydratedFruits.certifications.jas.description"),
      icon: "🇯🇵",
    },
    {
      name: t("dehydratedFruits.certifications.naturland.name"),
      image: "/images/certifications/Naturland.webp",
      alt: t("dehydratedFruits.certifications.naturland.name"),
      description: t("dehydratedFruits.certifications.naturland.description"),
      icon: "🌱",
    },
    {
      name: t("dehydratedFruits.certifications.fairtrade.name"),
      image: "/images/certifications/FairTrade.webp",
      alt: t("dehydratedFruits.certifications.fairtrade.name"),
      description: t("dehydratedFruits.certifications.fairtrade.description"),
      icon: "🤝",
    },
    {
      name: t("dehydratedFruits.certifications.fda.name"),
      image: "/images/certifications/FDA.webp",
      alt: t("dehydratedFruits.certifications.fda.name"),
      description: t("dehydratedFruits.certifications.fda.description"),
      icon: "✅",
    },
  ];

  const faqs = [
    {
      question: t("organicSpices.faqs.0.question"),
      answer: t("organicSpices.faqs.0.answer"),
    },
    {
      question: t("organicSpices.faqs.1.question"),
      answer: t("organicSpices.faqs.1.answer"),
    },
    {
      question: t("organicSpices.faqs.2.question"),
      answer: t("organicSpices.faqs.2.answer"),
    },
    {
      question: t("organicSpices.faqs.3.question"),
      answer: t("organicSpices.faqs.3.answer"),
    },
  ];

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="Organic Spices | Premium Sri Lankan Turmeric, Ginger, Cinnamon & Black Pepper"
        description="100% organic single-origin spices from Sri Lanka. BRC certified turmeric powder, ginger powder, Ceylon cinnamon, and black pepper. No additives, clean label, fully traceable. Available in 1kg, 2.5kg & custom packs."
        keywords="organic spices Sri Lanka, Ceylon cinnamon organic, organic turmeric powder, organic ginger powder, organic black pepper, single origin spices, clean label spices, BRC certified spices, USDA organic spices, EU organic spices, organic spice powder, Ceylon spices organic, curcumin turmeric, organic spice blends, wholesale organic spices, private label spices, organic seasoning, natural spices no additives, traceable organic spices, certified organic spices"
        image="https://worganaturals.com/images/products/turmeric-powder-pack.webp"
        url="/products/organic-spices"
        type="product"
        productSchema={true}
        category="Organic Spices & Seasonings"
        brand="Worga Naturals"
        availability="InStock"
        price="8.99"
        currency="USD"
        sku="OS-TUR-1KG"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Product Collection Schema with Variants */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Organic Spices Collection",
            description:
              "Premium organic single-origin spices from Sri Lanka. 100% pure turmeric, ginger, Ceylon cinnamon, and black pepper. BRC certified, clean label with no additives. Available in powder and whole formats.",
            brand: {
              "@type": "Brand",
              name: "Worga Naturals",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No. 152/G, Meepavita, Udamapitigama",
                addressLocality: "Dompe",
                postalCode: "11680",
                addressCountry: "LK",
              },
            },
            image: [
              "https://worganaturals.com/images/products/turmeric-powder-pack.webp",
              "https://worganaturals.com/images/products/ginger-powder-pack.webp",
              "https://worganaturals.com/images/products/cinnamon-ceylon-sticks.webp",
              "https://worganaturals.com/images/products/black-pepper-whole-pack.webp",
              "https://worganaturals.com/images/products/black-pepper-powder-pack.webp",
            ],
            category: "Organic Spices & Seasonings",
            offers: {
              "@type": "AggregateOffer",
              availability: "https://schema.org/InStock",
              priceCurrency: "USD",
              lowPrice: "7.99",
              highPrice: "24.99",
              offerCount: "5",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "243",
            },
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "Shelf Life",
                value: "18-24 months",
              },
              {
                "@type": "PropertyValue",
                name: "Pack Sizes",
                value: "1kg, 2.5kg, Custom",
              },
              {
                "@type": "PropertyValue",
                name: "Certifications",
                value:
                  "BRC, EU Organic, USDA Organic, JAS, Naturland, Fairtrade, FDA",
              },
              {
                "@type": "PropertyValue",
                name: "Origin",
                value: "Sri Lanka",
              },
              {
                "@type": "PropertyValue",
                name: "Processing",
                value: "HACCP & GMP Certified",
              },
              {
                "@type": "PropertyValue",
                name: "Label Type",
                value: "Clean Label - No Additives",
              },
            ],
            hasVariant: [
              {
                "@type": "Product",
                name: "Organic Turmeric Powder",
                description:
                  "100% organic turmeric powder with high curcumin content. Bright yellow-orange color, warm earthy aroma. Perfect for curry blends, golden milk, and supplements.",
                sku: "OS-TUR-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Active Compound",
                    value: "Curcumin",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Bright yellow to orange powder",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Shelf Life",
                    value: "24 months",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "8.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Ginger Powder",
                description:
                  "100% organic ginger powder with natural gingerols. Fresh, zesty aroma with warm spicy heat. Ideal for baking, beverages, and marinades.",
                sku: "OS-GIN-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Active Compound",
                    value: "Gingerols",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Light beige to tan powder",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Shelf Life",
                    value: "24 months",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "9.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Ceylon Cinnamon Sticks",
                description:
                  "Premium Ceylon cinnamon (Cinnamomum verum) in whole quills. Sweet, delicate aroma with mild flavor. Available as sticks or powder.",
                sku: "OS-CIN-STK-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Active Compound",
                    value: "Cinnamaldehyde",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Light brown quills",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Shelf Life",
                    value: "24 months",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Type",
                    value: "True Ceylon Cinnamon",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "18.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Black Pepper Whole",
                description:
                  "Premium organic black pepper whole berries. High piperine content with prominent pungent aroma. Perfect for grinding fresh or spice blends.",
                sku: "OS-BPW-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Active Compound",
                    value: "Piperine / Volatile oil",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Dark brown to black whole berries",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Shelf Life",
                    value: "24 months",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "12.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Black Pepper Powder",
                description:
                  "Finely ground organic black pepper powder. Fresh, pungent aroma with typical black pepper flavor. Ready-to-use for seasoning and blends.",
                sku: "OS-BPP-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Active Compound",
                    value: "Piperine / Volatile oil",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Ash to black color powder",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Shelf Life",
                    value: "18 months",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "11.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
            ],
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Are there any additives in Worga Naturals organic spices?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No—100% organic spices, nothing added. Our spices are single-ingredient products with no added sugars, colors, flavoring, or preservatives. Clean label certified.",
                },
              },
              {
                "@type": "Question",
                name: "What's the difference between whole spices and powder?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Both formats are offered where applicable. Whole spices have longer shelf life and can be ground fresh for maximum flavor. Powders are convenient and ready-to-use. Custom grind sizes are available for bulk orders.",
                },
              },
              {
                "@type": "Question",
                name: "Can I get private label or custom blends?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—custom milling, blends, and branding are available. We offer private label services with flexible pack sizes and custom formulations. Contact us for bulk and private label inquiries.",
                },
              },
              {
                "@type": "Question",
                name: "What is the shelf life of organic spices?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Turmeric, ginger, and cinnamon powders typically have 24 months shelf life. Black pepper powder has 18 months. Whole spices often last 24 months. Exact shelf life is confirmed per batch on the specification sheet.",
                },
              },
              {
                "@type": "Question",
                name: "Are Worga Naturals spices truly organic and certified?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—all our spices are certified by BRC, EU Organic, USDA (NOP), JAS, Naturland, and Fairtrade. Full traceability is maintained from farm to pack with identity-preserved organic supply chain and batch tracking.",
                },
              },
              {
                "@type": "Question",
                name: "What makes Ceylon cinnamon different from regular cinnamon?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Ceylon cinnamon (Cinnamomum verum) is 'true cinnamon' with a sweeter, more delicate flavor and lower coumarin content compared to Cassia cinnamon. It's prized for its premium quality and health benefits.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Products",
                item: "https://worganaturals.com/products",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Organic Spices",
                item: "https://worganaturals.com/products/organic-spices",
              },
            ],
          })}
        </script>

        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals",
            url: "https://worganaturals.com",
            logo: "https://worganaturals.com/images/logo.png",
            description:
              "Premium organic products from Sri Lanka including spices, coconut water, and natural preserves. BRC certified with full traceability.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+94-XX-XXXXXXX",
              contactType: "Customer Service",
              availableLanguage: ["en", "si"],
            },
            sameAs: [
              "https://www.facebook.com/worganaturals",
              "https://www.instagram.com/worganaturals",
              "https://www.linkedin.com/company/worganaturals",
            ],
          })}
        </script>

        {/* ItemList Schema for Product Collection */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Organic Spices Collection",
            description: "Premium organic spices from Sri Lanka",
            numberOfItems: 5,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "Product",
                  name: "Organic Turmeric Powder",
                  url: "https://worganaturals.com/products/organic-spices#turmeric",
                  image:
                    "https://worganaturals.com/images/products/turmeric-powder-pack.webp",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@type": "Product",
                  name: "Organic Ginger Powder",
                  url: "https://worganaturals.com/products/organic-spices#ginger",
                  image:
                    "https://worganaturals.com/images/products/ginger-powder-pack.webp",
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@type": "Product",
                  name: "Organic Ceylon Cinnamon",
                  url: "https://worganaturals.com/products/organic-spices#cinnamon",
                  image:
                    "https://worganaturals.com/images/products/cinnamon-ceylon-sticks.webp",
                },
              },
              {
                "@type": "ListItem",
                position: 4,
                item: {
                  "@type": "Product",
                  name: "Organic Black Pepper Whole",
                  url: "https://worganaturals.com/products/organic-spices#pepper-whole",
                  image:
                    "https://worganaturals.com/images/products/black-pepper-whole-pack.webp",
                },
              },
              {
                "@type": "ListItem",
                position: 5,
                item: {
                  "@type": "Product",
                  name: "Organic Black Pepper Powder",
                  url: "https://worganaturals.com/products/organic-spices#pepper-powder",
                  image:
                    "https://worganaturals.com/images/products/black-pepper-powder-pack.webp",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Download Modal */}
        <DownloadModal />
        {/* Spacer for navbar */}
        <div className="h-24"></div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-full text-sm font-semibold shadow-lg">
                {t("organicSpices.hero.badge")}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">
                {t("organicSpices.hero.title1")}{" "}
              </span>
              <span className="text-orange-600 dark:text-orange-500">
                {t("organicSpices.hero.title2")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto mb-8">
              {t("organicSpices.hero.description")}
            </p>

            {/* Traceability Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-full px-6 py-3 shadow-lg"
            >
              <FaBarcode className="text-orange-600 text-2xl" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {t("organicSpices.hero.traceability")}
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* Common Features */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("organicSpices.whyChoose")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {commonFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-orange-600 mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Applications Section */}
            <div className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                {t("organicSpices.idealFor")}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {applications.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/30 transition-all duration-300"
                  >
                    <div className="text-3xl mb-2 flex justify-center">
                      {item.icon}
                    </div>
                    <p className="text-sm font-semibold">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Product Family */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("organicSpices.collection")}
            </h2>

            {/* Product Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {products.map((product, index) => (
                <button
                  key={index}
                  onClick={() => setActiveProduct(index)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeProduct === index
                      ? "bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg scale-105"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md"
                  }`}
                >
                  <span className="hidden lg:inline">
                    {product.name.split(" ")[1]} {product.name.split(" ")[2]}
                  </span>
                  <span className="lg:hidden">{product.emoji}</span>
                </button>
              ))}
            </div>

            {/* Active Product Card */}
            <motion.div
              key={activeProduct}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="grid md:grid-cols-2 gap-8">
                {/* Product Image Slider */}
                <div
                  className={`bg-gradient-to-br ${products[activeProduct].color} flex items-center justify-center p-12 md:p-16 relative overflow-hidden`}
                >
                  {/* Image Display */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeImageIndex}
                      initial={{ opacity: 0, scale: 0.9, x: 100 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.9, x: -100 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      className="w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={products[activeProduct].images[activeImageIndex]}
                        alt={products[activeProduct].name}
                        className="max-w-full max-h-[500px] object-contain drop-shadow-2xl rounded-lg"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `<div class="text-9xl">${products[activeProduct].emoji}</div>`;
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>

                  {/* Navigation Arrows */}
                  {products[activeProduct].images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
                        aria-label={t("organicSpices.imageNav.previous")}
                      >
                        <FaChevronLeft className="text-red-600 text-2xl" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
                        aria-label={t("organicSpices.imageNav.next")}
                      >
                        <FaChevronRight className="text-red-600 text-2xl" />
                      </button>
                    </>
                  )}

                  {/* Image Indicators */}
                  {products[activeProduct].images.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 bg-black/30 backdrop-blur-md rounded-full px-4 py-2 z-10">
                      {products[activeProduct].images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveImageIndex(index)}
                          className={`transition-all duration-300 rounded-full ${
                            activeImageIndex === index
                              ? "bg-white w-10 h-3"
                              : "bg-white/50 hover:bg-white/75 w-3 h-3"
                          }`}
                          aria-label={t("organicSpices.imageNav.goTo", {
                            number: index + 1,
                          })}
                        />
                      ))}
                    </div>
                  )}

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold z-10">
                    {activeImageIndex + 1} {t("organicSpices.imageCounter.of")}{" "}
                    {products[activeProduct].images.length}
                  </div>

                  {/* Floating spice particles */}
                  <motion.div
                    animate={{
                      y: [0, -100],
                      x: [0, 20],
                      opacity: [0.8, 0],
                    }}
                    transition={{ duration: 3, repeat: Infinity, delay: 0 }}
                    className="absolute bottom-20 left-20 text-4xl opacity-50"
                  >
                    ✨
                  </motion.div>
                  <motion.div
                    animate={{
                      y: [0, -120],
                      x: [0, -30],
                      opacity: [0.6, 0],
                    }}
                    transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
                    className="absolute bottom-32 right-24 text-3xl opacity-40"
                  >
                    ✨
                  </motion.div>
                  <motion.div
                    animate={{
                      y: [0, -90],
                      x: [0, 15],
                      opacity: [0.7, 0],
                    }}
                    transition={{ duration: 2.8, repeat: Infinity, delay: 1 }}
                    className="absolute top-32 left-32 text-3xl opacity-40"
                  >
                    🌶️
                  </motion.div>
                </div>

                {/* Product Details */}
                <div className="p-8 md:p-12">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                      {t("organicSpices.badges.organic")}
                    </span>
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {t("organicSpices.badges.singleIngredient")}
                    </span>
                  </div>

                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                    {products[activeProduct].name}
                  </h3>

                  {/* Sensory Profile */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <span className="font-bold text-orange-600">
                        {t("organicSpices.sensory.appearance")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].appearance}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-orange-600">
                        {t("organicSpices.sensory.aroma")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].aroma}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-orange-600">
                        {t("organicSpices.sensory.flavour")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].flavour}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-orange-600">
                        {t("organicSpices.sensory.texture")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].texture}
                      </span>
                    </div>
                  </div>

                  {/* Ingredient */}
                  <div className="bg-orange-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                    <h4 className="font-bold text-orange-600 dark:text-orange-400 mb-2">
                      {t("organicSpices.ingredient.title")}:
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-lg">
                      {products[activeProduct].ingredient}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      <strong>{t("organicSpices.ingredient.freeFrom")}:</strong>{" "}
                      {t("organicSpices.ingredient.freeFromList")}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <strong>
                        {t("organicSpices.ingredient.activeCompound")}:
                      </strong>{" "}
                      {products[activeProduct].active}
                    </p>
                  </div>

                  {/* Uses */}
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      {t("organicSpices.commonUses")}:
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      {products[activeProduct].uses.map((use, i) => (
                        <div
                          key={i}
                          className="flex items-center space-x-2 bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg p-3"
                        >
                          <FaCheckCircle className="text-orange-600 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300 text-sm">
                            {use}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Common Specs */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t("organicSpices.specs.origin")}:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t("organicSpices.specs.originValue")}
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t("organicSpices.specs.shelfLife")}:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {products[activeProduct].shelfLife}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t("organicSpices.specs.packSizes")}:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t("organicSpices.specs.packSizesValue")}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t("organicSpices.specs.packaging")}:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t("organicSpices.specs.packagingValue")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Process Description */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-8 text-white shadow-xl"
          >
            <div className="flex items-start space-x-4">
              <FaMortarPestle className="text-5xl flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-bold mb-3">
                  {t("organicSpices.process.title")}
                </h3>
                <p className="text-lg leading-relaxed">
                  {t("organicSpices.process.description")}
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Certifications */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("dehydratedFruits.certifications.title")}
            </h2>

            <div className="flex flex-wrap justify-center items-center gap-6 max-w-5xl mx-auto">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-16 h-16 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-xl p-2 group-hover:bg-orange-100 dark:group-hover:bg-gray-600 transition-colors">
                      <img
                        src={cert.image}
                        alt={cert.alt}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = document.createElement("span");
                          fallback.className = "text-2xl";
                          fallback.textContent = cert.icon;
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center max-w-[80px]">
                      {cert.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality & Safety */}
        <section className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("organicSpices.quality.title")}
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    icon: <FaShieldAlt />,
                    title: t("organicSpices.quality.0.title"),
                    desc: t("organicSpices.quality.0.desc"),
                  },
                  {
                    icon: <FaBarcode />,
                    title: t("organicSpices.quality.1.title"),
                    desc: t("organicSpices.quality.1.desc"),
                  },
                  {
                    icon: <FaCertificate />,
                    title: t("organicSpices.quality.2.title"),
                    desc: t("organicSpices.quality.2.desc"),
                  },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="text-5xl text-orange-600 mb-4 flex justify-center">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {item.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Usage & Storage */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              {t("organicSpices.usageStorage.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <FaBox />,
                  title: t("organicSpices.usageStorage.0.title"),
                  text: t("organicSpices.usageStorage.0.text"),
                },
                {
                  icon: <FaBoxOpen />,
                  title: t("organicSpices.usageStorage.1.title"),
                  text: t("organicSpices.usageStorage.1.text"),
                },
                {
                  icon: <FaBlender />,
                  title: t("organicSpices.usageStorage.2.title"),
                  text: t("organicSpices.usageStorage.2.text"),
                },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-5xl text-orange-600 mb-4 flex justify-center">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Specs Highlight */}
        <section className="bg-gradient-to-r from-orange-600 to-red-600 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto text-center text-white"
            >
              <FaIndustry className="text-6xl mb-6 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("organicSpices.technicalSpecs.title")}
              </h2>
              <div className="grid md:grid-cols-2 gap-6 text-left">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="font-bold text-xl mb-3">
                    {t("organicSpices.technicalSpecs.physical.title")}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li>✓ {t("organicSpices.technicalSpecs.physical.0")}</li>
                    <li>✓ {t("organicSpices.technicalSpecs.physical.1")}</li>
                    <li>✓ {t("organicSpices.technicalSpecs.physical.2")}</li>
                  </ul>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="font-bold text-xl mb-3">
                    {t("organicSpices.technicalSpecs.qualityLogistics.title")}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li>
                      ✓ {t("organicSpices.technicalSpecs.qualityLogistics.0")}
                    </li>
                    <li>
                      ✓ {t("organicSpices.technicalSpecs.qualityLogistics.1")}
                    </li>
                    <li>
                      ✓ {t("organicSpices.technicalSpecs.qualityLogistics.2")}
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQs */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("organicSpices.faqsTitle")}
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-orange-50 dark:hover:bg-gray-700 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-4">
                      <FaQuestionCircle className="text-orange-600 text-2xl flex-shrink-0" />
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {faq.question}
                      </h3>
                    </div>
                    {openFaq === index ? (
                      <FaChevronUp className="text-orange-600 text-xl flex-shrink-0" />
                    ) : (
                      <FaChevronDown className="text-orange-600 text-xl flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed pl-10">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Technical Specs Teaser */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl"
          >
            <div className="text-center">
              <FaDownload className="text-6xl mb-4 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("organicSpices.technical.title")}
              </h2>
              <p className="text-lg mb-6 opacity-90">
                {t("organicSpices.technical.description")}
              </p>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="bg-white text-indigo-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 flex items-center space-x-2 mx-auto"
              >
                <FaDownload />
                <span>{t("organicSpices.technical.downloadButton")}</span>
              </button>
            </div>
          </motion.div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default OrganicSpices;
